import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Table, Container, Row, Col, Card, InputGroup, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useUsuarios from '../../services/hooks/useUsuarios';

const MantenimientoUsuarios = () => {
  const { 
    usuarios, 
    loading, 
    fetchUsuarios, 
    createUsuario, 
    updateUsuario, 
    deleteUsuario 
  } = useUsuarios();
  
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    nombre: '',
    username: '',
    email: '',
    password: '',
    numero_documento: '',
    acceso_corporativo: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchUsuarios();
  }, []);


  const handleShow = (user = null) => {
    if (user) {
      setCurrentUser({
        ...user,
        username: user.username || '',
        password: '', // No mostrar password actual
        acceso_corporativo: user.acceso_corporativo || false
      });
      setIsEditing(true);
    } else {
      setCurrentUser({
        nombre: '',
        username: '',
        email: '',
        password: '',
        numero_documento: '',
        acceso_corporativo: false
      });
      setIsEditing(false);
    }
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentUser({
      nombre: '',
      username: '',
      email: '',
      password: '',
      numero_documento: '',
      acceso_corporativo: false
    });
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentUser(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser.nombre || !currentUser.username || !currentUser.email || !currentUser.numero_documento) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    if (!isEditing && !currentUser.password) {
      toast.error('La contraseña es obligatoria para usuarios nuevos');
      return;
    }

    try {
      // Preparar datos para envío
      const dataToSend = {
        nombre: currentUser.nombre,
        username: currentUser.username,
        email: currentUser.email,
        numero_documento: parseInt(currentUser.numero_documento),
        acceso_corporativo: currentUser.acceso_corporativo || false
      };

      // Solo incluir password si se está editando y se proporcionó uno nuevo, o si es un usuario nuevo
      if (currentUser.password) {
        dataToSend.password = currentUser.password;
      }

      if (isEditing) {
        await updateUsuario(currentUser.id, dataToSend);
      } else {
        await createUsuario(dataToSend);
      }
      
      handleClose();
    } catch (error) {
      // Los errores ya son manejados por el hook
      console.error('Error en handleSubmit:', error);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este usuario?')) {
      try {
        await deleteUsuario(userId);
      } catch (error) {
        // Los errores ya son manejados por el hook
        console.error('Error en handleDelete:', error);
      }
    }
  };

  // Asegurar que usuarios sea siempre un array antes de filtrar
  const filteredUsuarios = (Array.isArray(usuarios) ? usuarios : [])
    .filter(usuario => usuario != null) // Filtrar elementos null/undefined
    .filter(usuario =>
      usuario.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.numero_documento?.toString().includes(searchTerm)
    );

  return (
    <Container fluid className="py-4">
      <style>
        {`
          .card-dh {
            border: none;
            border-radius: 1rem;
            box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .card-header-dh {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 1rem 1rem 0 0;
            padding: 1.5rem;
            border: none;
          }

          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 8px;
            padding: 8px 16px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            color: white;
          }

          .btn-dh-warning {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border: none;
            color: white;
            border-radius: 8px;
            padding: 8px 16px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f5576c 0%, #e11d48 100%);
            transform: translateY(-2px);
            color: white;
          }

          .btn-dh-danger {
            background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
            border: none;
            color: white;
            border-radius: 8px;
            padding: 8px 16px;
            font-weight: 600;
            transition: all 0.3s ease;
          }

          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #ff4b2b 0%, #dc2626 100%);
            transform: translateY(-2px);
            color: white;
          }

          .table-dh {
            border-collapse: separate;
            border-spacing: 0;
            background: white;
            border-radius: 0.75rem;
            overflow: hidden;
          }

          .table-dh thead th {
            background: #374151;
            color: white;
            font-weight: 700;
            border: none;
            padding: 1rem;
          }

          .table-dh tbody tr {
            transition: all 0.2s ease;
          }

          .table-dh tbody tr:hover {
            background: #f8f9fa;
            transform: translateY(-1px);
          }

          .table-dh tbody td {
            padding: 1rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
          }

          .search-box {
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            transition: all 0.3s ease;
          }

          .search-box:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.15);
          }

          .custom-checkbox .form-check-input:checked {
            background-color: #667eea;
            border-color: #667eea;
          }

          .custom-checkbox .form-check-input:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }

          .custom-checkbox .form-check-label {
            cursor: pointer;
            padding-left: 0.5rem;
          }

          .custom-checkbox {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 1rem;
            margin-top: 0.5rem;
            transition: all 0.3s ease;
          }

          .custom-checkbox:hover {
            background: #f1f5f9;
            border-color: #cbd5e1;
          }
        `}
      </style>
      
      <ToastContainer position="bottom-right" />
      
      <Card className="card-dh">
        <Card.Header className="card-header-dh">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0">Gestión de Usuarios</h4>
              <p className="mb-0 opacity-75">Administrar usuarios del sistema</p>
            </Col>
            <Col xs="auto">
              <Button 
                className="btn-dh-primary d-flex align-items-center gap-2"
                onClick={() => handleShow()}
              >
                <FaPlus /> Nuevo Usuario
              </Button>
            </Col>
          </Row>
        </Card.Header>
        
        <Card.Body className="p-4">
          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nombre, email o documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-box"
                />
              </InputGroup>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : (
            <Table responsive className="table-dh">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Documento</th>
                  <th>Acceso Corporativo</th>
                  <th>Fecha Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.length > 0 ? (
                  filteredUsuarios.map((usuario) => (
                    usuario && (
                    <tr key={usuario.id}>
                      <td>{usuario.id}</td>
                      <td className="fw-semibold">{usuario.nombre || '-'}</td>
                      <td>{usuario.username || '-'}</td>
                      <td>{usuario.email || '-'}</td>
                      <td>{usuario.numero_documento || '-'}</td>
                      <td>
                        <span
                          className={`badge ${usuario.acceso_corporativo ? 'bg-success' : 'bg-secondary'}`}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {usuario.acceso_corporativo ? 'Sí' : 'No'}
                        </span>
                      </td>
                      <td>{usuario.created_at ? new Date(usuario.created_at).toLocaleDateString('es') : '-'}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            className="btn-dh-warning"
                            onClick={() => handleShow(usuario)}
                            title="Editar"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            className="btn-dh-danger"
                            onClick={() => handleDelete(usuario.id)}
                            title="Eliminar"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    )
                  )).filter(Boolean)
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios registrados'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para crear/editar usuario */}
      <Modal show={showModal} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Modal.Title>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={currentUser.nombre}
                    onChange={handleInputChange}
                    required
                    placeholder="Ingrese el nombre completo"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de Usuario <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={currentUser.username}
                    onChange={handleInputChange}
                    required
                    placeholder="Ej: yosi.rivera o yrivera"
                  />
                  <Form.Text className="text-muted">
                    Este será el nombre de usuario para iniciar sesión
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={currentUser.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Ingrese el email"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Número de Documento <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="numero_documento"
                    value={currentUser.numero_documento}
                    onChange={handleInputChange}
                    required
                    placeholder="Ingrese el número de documento"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Contraseña {!isEditing && <span className="text-danger">*</span>}
                    {isEditing && <small className="text-muted">(dejar vacío para mantener actual)</small>}
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={currentUser.password}
                      onChange={handleInputChange}
                      required={!isEditing}
                      placeholder={isEditing ? "Nueva contraseña (opcional)" : "Ingrese la contraseña"}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ borderColor: '#ced4da' }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            {/* Checkbox para acceso corporativo */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="acceso_corporativo"
                    name="acceso_corporativo"
                    checked={currentUser.acceso_corporativo}
                    onChange={handleInputChange}
                    label={
                      <span>
                        <strong>Acceso Corporativo</strong>
                        <br />
                        <small className="text-muted">
                          Permite al usuario acceder a la vista corporativa con resumen gerencial de todos los proyectos
                        </small>
                      </span>
                    }
                    className="custom-checkbox"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            className="btn-dh-primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MantenimientoUsuarios;